import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { IUser, IUserNotification } from '@core/models';
import { Authentication, User } from '@core/services';
import { NgbPopover, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { firstValueFrom } from 'rxjs';

/**
 * ### **NotificationsComponent**
 *
 * **Selector:** ``<app-notifications>``
 *
 * Un componente es un bloque, que contiene un template, contiene estilos y contiene la lógica, en este caso es el Componente
 * encargado de renderizar la seccion de notificaciones.
 */
@Component({
  selector: 'app-notifications',
  imports: [NgbPopoverModule, TranslateModule, DatePipe, RouterModule, NgxSkeletonLoaderModule],
  templateUrl: "./notifications.component.html",
  styleUrl: "./notifications.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Notifications {
  private readonly authService = inject(Authentication);
  private readonly userService = inject(User);

  /** Variable que guardará la lista de notificaciones*/
  protected readonly notifications = signal<IUserNotification[] | null>(null);
  /** Variable que guardará la información del usuario*/
  protected readonly auth = signal<IUser | null>(null);
  /** Parametro permite el acceso directo y configuracion del componente html*/
  private readonly popover = viewChild<NgbPopover>('p');

  /** @ignore*/
  constructor() {
    // Obtiene la informacion de autenticación
    this.getAuth();
    // Se suscribe al servicio de autenticación para saber si el usuario esta autenticado
    this.authService.onAuthenticate.pipe(takeUntilDestroyed()).subscribe(() => this.getAuth());

    // se suscribe al metodo onlogout ṕara sasber si el usuario esta desconectado, si es asi notifica auth como un valor nulo.
    this.authService.onLogout.pipe(takeUntilDestroyed()).subscribe(() => this.auth.set(null));
  }

  /**
   * Función encargada de obtener la información de autenticación.
   */
  private getAuth(): void {
    this.auth.set(this.authService.getAuthInformation());
  }

  /**
   * Función encargada de obtener las notificaciones para un usuario, para esto hace uso del
   * servicio UserService y su metodo notifications().
   */
  protected async getNotifications(): Promise<void> {
    try {
      this.notifications.set(null);
      const notifications = await firstValueFrom(
        this.userService.notifications(Number(this.auth()?.id)),
      );
      // console.log(notifications);
      setTimeout(() => this.notifications.set(notifications || null), 500);
    } catch (error) {
      console.error(error);
    }
  }
}
